<template>
  <div :class="['about-page', `lang-${$i18n.locale}`]">
    <div class="about-inner">
      <div class="sidebar">
        <div class="sidebar-fixed">
          <a
            href="#mission"
            :class="['sidebar-btn', { active: activeTab === 'mission' }]"
            @click.prevent="goTo('mission')"
            v-html="$t('about.missionBtn')"
          >
          </a>
          <a
            href="#openAccess"
            :class="['sidebar-btn', { active: activeTab === 'openAccess' }]"
            @click.prevent="goTo('openAccess')"
            v-html="$t('about.openAccessBtn')"
          >
          </a>
        </div>
      </div>
      <div class="content">
        <section id="mission" ref="mission" class="content-section">
          <h2>{{ $t("about.missionTitle") }}</h2>
          <div
            class="text-content"
            v-html="formatText($t('about.missionContent'))"
          ></div>
        </section>
        <div class="section-divider"></div>
        <section id="openAccess" ref="openAccess" class="content-section">
          <h2>{{ $t("about.openAccessTitle") }}</h2>
          <div
            class="text-content"
            v-html="formatText($t('about.openAccessContent'))"
          ></div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "AboutPage",
  data() {
    return {
      activeTab: "mission",
      isProgrammaticScroll: false,
    };
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll, { passive: true });
    this.$nextTick(() => {
      this.handleInitialHash();
      this.onScroll();
    });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    if (this.scrollTimer) clearTimeout(this.scrollTimer);
  },
  methods: {
    formatText(text) {
      return text
        .split(/\n\n+/)
        .map((para) => {
          if (/^\s*<(ul|ol|div|h\d|table)/i.test(para)) return para;
          return `<p>${para.replace(/\n/g, "<br>")}</p>`;
        })
        .join("");
    },
    handleInitialHash() {
      const hash = window.location.hash.replace("#", "");
      if (hash && this.$refs[hash]) {
        this.goTo(hash);
      }
    },
    goTo(tab) {
      this.activeTab = tab;
      const el = this.$refs[tab];
      if (!el) return;
      this.isProgrammaticScroll = true;

      // 找到被点击的具体按钮元素（而非容器）
      const clickedBtn = this.$el.querySelector(`.sidebar-btn[href="#${tab}"]`);
      const h2 = el.querySelector("h2") || el;
      const targetY = clickedBtn
        ? clickedBtn.getBoundingClientRect().top
        : this.$i18n.locale === "en"
          ? 185
          : 180;

      const rect = h2.getBoundingClientRect();
      const top = rect.top + window.pageYOffset - targetY;
      window.scrollTo({ top, behavior: "smooth" });

      if (history.replaceState) {
        history.replaceState(null, "", `#${tab}`);
      }

      // smooth 滚动结束后做像素级精确修正
      clearTimeout(this.scrollTimer);
      clearTimeout(this._fixTimer);
      this._fixTimer = setTimeout(() => {
        const currentH2Top = h2.getBoundingClientRect().top;
        const currentBtnTop = clickedBtn
          ? clickedBtn.getBoundingClientRect().top
          : targetY;
        const diff = currentH2Top - currentBtnTop;
        if (Math.abs(diff) > 1) {
          window.scrollBy({ top: diff, behavior: "instant" });
        }
      }, 700);

      this.scrollTimer = setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, 1200);
    },
    onScroll() {
      if (this.isProgrammaticScroll) return;
      const oa = this.$refs.openAccess;
      if (!oa) return;

      // 用与 goTo 一致的方式计算阈值：h2 顶部对齐对应按钮
      const h2 = oa.querySelector("h2") || oa;
      const oaBtn = this.$el.querySelector('.sidebar-btn[href="#openAccess"]');
      const targetY = oaBtn
        ? oaBtn.getBoundingClientRect().top
        : this.$i18n.locale === "en"
          ? 200
          : 180;

      // 当 openAccess 的 h2 滚过目标线时切换 activeTab
      const top = h2.getBoundingClientRect().top;
      this.activeTab = top <= targetY ? "openAccess" : "mission";
    },
  },
};
</script>

<style lang="scss" scoped>
.about-page {
  flex: 1;
  padding: 40px 0;
}

.about-inner {
  max-width: $max-width;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  gap: 40px;
  align-items: flex-start;
}

/* ===== Sidebar ===== */
.sidebar {
  flex-shrink: 0;
  width: 180px;
}

.sidebar-fixed {
  position: fixed;
  top: 180px;
  width: 180px;
  left: max(24px, calc((100vw - #{$max-width}) / 2 + 24px));
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 10;
}

.sidebar-btn {
  display: block;
  padding: 12px 10px;
  border: 1px solid $primary-orange;
  border-radius: 24px;
  font-size: 16px;
  background: $bg-orange-light;
  color: $text-dark;
  text-align: center;
  text-decoration: none;
  font-family: inherit;
  cursor: pointer;
  white-space: normal;
  line-height: 1.4;
  transition: all 0.2s;

  &:hover:not(.active) {
    background: lighten($primary-orange, 35%);
  }

  &.active {
    background: $primary-orange;
    color: $white;
    font-weight: 600;
  }
}

/* ===== Content ===== */
.content {
  flex: 1;
  min-width: 0;
}

.content-section {
  scroll-margin-top: 180px;

  h2 {
    font-size: 22px;
    color: $text-dark;
    font-weight: 700;
    margin-bottom: 20px;
  }
}

.section-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.1);
  margin: 36px 0;
}
.about-page.lang-en .sidebar-fixed {
  top: 185px;
}
.about-page.lang-en .sidebar-btn {
  padding: 12px 16px;
  white-space: normal;
  line-height: 1.4;
}
.about-page.lang-en .content-section {
  scroll-margin-top: 185px;
}
.about-page.lang-en .text-content ::v-deep p {
  line-height: 1.5;
  letter-spacing: 0px;
}
.about-page.lang-en .text-content ::v-deep .about-list li {
  line-height: 1.6;
  letter-spacing: 0px;
}
.text-content {
  ::v-deep p {
    font-size: 16px;
    line-height: 1.9;
    color: $text-dark;
    letter-spacing: 1px;
    margin: 0 0 18px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ::v-deep .about-list {
    list-style: none;
    padding-left: 24px;
    margin: 0 0 18px;

    li {
      font-size: 16px;
      line-height: 2;
      color: $text-dark;
      letter-spacing: 1px;
      position: relative;
      padding-left: 18px;

      &::before {
        content: "-";
        position: absolute;
        left: 0;
        color: $text-dark;
      }
    }
  }

  ::v-deep .brand-color {
    color: $primary-orange;
    font-weight: 600;
  }

  ::v-deep .license-link {
    color: #409eff;
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
